<template>
  <section class="service-page" :dir="pageDirection">
    <div v-if="loading" class="page-loading">
      <div class="loader-card">
        <div class="loader-ring"></div>

        <h3>{{ tr("loadingTitle") }}</h3>

        <p>{{ tr("loadingText") }}</p>
      </div>
    </div>

    <div class="page-header">
      <h1>{{ tr("pageTitle") }}</h1>

      <p>{{ tr("pageSubtitle") }}</p>
    </div>

    <div class="top-row">
      <div class="counter">
        {{ facilities.length }} {{ tr("facilityUnit") }}
      </div>

      <select>
        <option>{{ tr("pageTitle") }}</option>
      </select>
    </div>

    <!-- مؤشرات المحور -->

    <div class="axis-summary">
      <div class="axis-card-header">
        <div>
          <h2>{{ tr("axisTitle") }} <small>(SRV)</small></h2>

          <p>{{ tr("axisDescription") }}</p>
        </div>

        <div class="axis-score">
          <span> {{ axisAverage !== null ? `${axisAverage}%` : "-" }}</span>

          <small>
            {{ tr("fisWeight") }}:

            {{ axisWeight !== null ? `${axisWeight}%` : "-" }}</small
          >
        </div>
      </div>

      <div class="axis-stats">
        <div>
          <b>{{ serviceKpis.length }}</b>

          <span>{{ tr("indicatorsCount") }}</span>
        </div>

        <div>
          <b>{{ healthFacilities.length }}</b>

          <span>{{ tr("sampleFacilities") }}</span>
        </div>

        <div>
          <b>{{ serviceCriticalCount }}</b>

          <span>{{ tr("criticalIndicators") }}</span>
        </div>
      </div>
    </div>

    <div class="service-kpi-grid">
      <article
        v-for="kpi in serviceKpis"
        :key="kpi.code"
        class="service-kpi-card"
        :class="{ flag: kpiStatus(kpi) === 'r' }"
        :data-critical-label="tr('criticalFlag')"
      >
        <div class="service-kpi-top">
          <span class="code">{{ kpi.code }}</span>

          <span v-if="kpi.composite" class="composite">{{
            tr("composite")
          }}</span>
        </div>

        <h3>{{ kpi.name }}</h3>

        <div class="service-kpi-value">
          <strong :style="{ color: statusColor(kpi) }">{{ kpi.val }}</strong>

          <span>{{ kpi.unit }}</span>

          <em
            >{{ tr("standard") }} {{ kpi.dir === "lt" ? "≤" : "≥" }}

            {{ kpi.std }}</em
          >
        </div>

        <div class="gauge">
          <div
            :style="{
              width: barWidth(kpi) + '%',

              background: statusColor(kpi),
            }"
          ></div>
        </div>

        <div class="service-kpi-foot">
          <span>
            {{ tr("achievement") }}:

            <b :style="{ color: statusColor(kpi) }">{{ achieve(kpi) }}%</b>
          </span>

          <span :class="['rag', kpiStatus(kpi)]">
            {{ statusLabel[kpiStatus(kpi)] }}
          </span>
        </div>

        <div v-if="kpi.chart" class="axis-mini-chart">
          <h4>{{ kpi.chart.label }}</h4>

          <apexchart
            :type="kpiApex(kpi).type"
            height="240"
            :series="kpiApex(kpi).series"
            :options="kpiApex(kpi).options"
          />
        </div>
      </article>
    </div>

    <div class="axis-card hidden">
      <div v-if="facilities.length" class="charts-grid">
        <div class="chart-card">
          <div class="section-title">
            {{ tr("interruptedServicesByFacility") }}
          </div>

          <apexchart
            type="bar"
            height="320"
            :series="[
              {
                name: tr('interruptedServices'),

                data: facilities.map((x) => x.interrupted_services),
              },
            ]"
            :options="barOptions(facilities.map((x) => x.facility_name))"
          />
        </div>

        <div class="chart-card">
          <div class="section-title">
            {{ tr("visitsBeforeAfterByFacility") }}
          </div>

          <apexchart
            type="bar"
            height="320"
            :series="facilityVisitsSeries"
            :options="barOptions(facilities.map((x) => x.facility_name))"
          />
        </div>

        <div class="chart-card">
          <div class="section-title">
            {{ tr("riskDistribution") }}
          </div>

          <apexchart
            v-if="riskLevelSeries.length"
            type="donut"
            height="330"
            :series="riskLevelSeries"
            :options="donutOptions(riskLevelLabels)"
          />

          <div v-else class="empty-chart">
            {{ tr("noData") }}
          </div>
        </div>

        <div class="chart-card">
          <div class="section-title">
            {{ tr("serviceStatusDistribution") }}
          </div>

          <apexchart
            v-if="statusSeries.length"
            type="donut"
            height="330"
            :series="statusSeries"
            :options="donutOptions(statusLabels)"
          />

          <div v-else class="empty-chart">
            {{ tr("noData") }}
          </div>
        </div>
      </div>

      <div class="filter-box">
        <input
          v-model="filters.search"
          type="text"
          :placeholder="tr('searchPlaceholder')"
        />

        <select v-model="filters.risk">
          <option :value="null">{{ tr("allRiskLevels") }}</option>

          <option v-for="item in riskItems" :key="item" :value="item">
            {{ item }}
          </option>
        </select>
      </div>

      <div class="services-table-wrap">
        <div class="table-title">
          {{ tr("facilityDetails") }}

          <span>{{ filteredFacilities.length }} {{ tr("facilityUnit") }}</span>
        </div>

        <table class="services-table">
          <thead>
            <tr>
              <th>{{ tr("facility") }}</th>

              <th>{{ tr("totalServices") }}</th>

              <th>{{ tr("interruptedServices") }}</th>

              <th>{{ tr("beforeCrisis") }}</th>

              <th>{{ tr("afterCrisis") }}</th>

              <th>{{ tr("dropRate") }}</th>

              <th>{{ tr("riskLevel") }}</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="item in paginatedFacilities" :key="item.facility_name">
              <td class="name">{{ item.facility_name }}</td>

              <td>{{ item.total_services }}</td>

              <td>{{ item.interrupted_services }}</td>

              <td>{{ item.pre_visits }}</td>

              <td>{{ item.post_visits }}</td>

              <td>{{ item.drop_rate }}%</td>

              <td>
                <span class="chip" :class="riskClass(item.highest_risk)">
                  {{ displayRisk(item.highest_risk) }}
                </span>
              </td>
            </tr>

            <tr v-if="!paginatedFacilities.length">
              <td colspan="7" class="empty">{{ tr("noMatchingData") }}</td>
            </tr>
          </tbody>
        </table>

        <div class="mobile-facility-cards">
          <article
            v-for="item in paginatedFacilities"
            :key="`mobile-${item.facility_name}`"
            class="mobile-facility-card"
          >
            <div class="mobile-card-head">
              <h3>{{ item.facility_name }}</h3>

              <span class="chip" :class="riskClass(item.highest_risk)">
                {{ displayRisk(item.highest_risk) }}
              </span>
            </div>

            <div class="mobile-card-body">
              <div class="mobile-data-row">
                <span>{{ tr("totalServices") }}</span>

                <strong>{{ item.total_services }}</strong>
              </div>

              <div class="mobile-data-row">
                <span>{{ tr("interruptedServices") }}</span>

                <strong>{{ item.interrupted_services }}</strong>
              </div>

              <div class="mobile-data-row">
                <span>{{ tr("beforeCrisis") }}</span>

                <strong>{{ item.pre_visits }}</strong>
              </div>

              <div class="mobile-data-row">
                <span>{{ tr("afterCrisis") }}</span>

                <strong>{{ item.post_visits }}</strong>
              </div>

              <div class="mobile-data-row">
                <span>{{ tr("dropRate") }}</span>

                <strong>{{ item.drop_rate }}%</strong>
              </div>
            </div>
          </article>

          <div v-if="!paginatedFacilities.length" class="mobile-empty">
            {{ tr("noMatchingData") }}
          </div>
        </div>

        <div class="pagination-footer">
          <div>
            {{
              tr("showing", {
                shown: paginatedFacilities.length,

                total: filteredFacilities.length,
              })
            }}
          </div>

          <div class="pager-controls">
            <select v-model.number="itemsPerPage">
              <option :value="5">5</option>

              <option :value="10">10</option>

              <option :value="20">20</option>

              <option :value="50">50</option>
            </select>

            <button :disabled="currentPage <= 1" @click="currentPage--">
              {{ tr("previous") }}
            </button>

            <span>{{ currentPage }} / {{ pageCount }}</span>

            <button :disabled="currentPage >= pageCount" @click="currentPage++">
              {{ tr("next") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";

import { useI18n } from "vue-i18n";

import api from "@/plugins/api";

const { t, locale } = useI18n();

const pageDirection = computed(() => (locale.value === "ar" ? "rtl" : "ltr"));

const isArabic = computed(() => locale.value === "ar");

const tr = (key, params = {}) => t(`servicePage.${key}`, params);

const services = ref([]);

const healthFacilities = ref([]);

const loading = ref(false);

const srv01 = ref(0);

const srv03 = ref(0);

const srv04 = ref(0);

const srv05 = ref(0);

const srv06 = ref(0);

const srv03Components = ref([]);

const srv06Components = ref([]);

const filters = ref({
  search: "",

  risk: null,
});

const currentPage = ref(1);

const itemsPerPage = ref(10);

const statusLabel = computed(() => ({
  g: tr("compliant"),

  a: tr("near"),

  r: tr("gap"),
}));

const displayRisk = (value) => {
  const key = String(value || "unspecified")
    .trim()

    .toLowerCase();

  const map = {
    حرج: "critical",

    critical: "critical",

    مرتفع: "high",

    عالي: "high",

    high: "high",

    متوسط: "medium",

    medium: "medium",

    منخفض: "low",

    خفيف: "low",

    low: "low",

    "غير محدد": "unspecified",

    unspecified: "unspecified",
  };

  return tr(`risk.${map[key] || "unspecified"}`);
};

const colors = { g: "#10b981", a: "#f59e0b", r: "#ef4444" };

const normalizeArray = (payload) => {
  if (Array.isArray(payload)) return payload;

  if (Array.isArray(payload?.data)) return payload.data;

  if (Array.isArray(payload?.data?.data)) return payload.data.data;

  if (Array.isArray(payload?.results)) return payload.results;

  return [];
};

const getApiNumber = (res) => {
  const d = res?.data;

  return Number(
    d?.summary?.avg_disruption_rate ??
      d?.summary?.avg_medicine_availability ??
      d?.summary?.avg_rate ??
      d?.summary?.average ??
      d?.summary?.value ??
      d?.data?.summary?.avg_disruption_rate ??
      d?.data?.summary?.avg_medicine_availability ??
      d?.data?.summary?.avg_rate ??
      d?.avg_disruption_rate ??
      d?.srv01_pct ??
      d?.srv04_pct ??
      d?.avg_rate ??
      d?.rate ??
      d?.percentage ??
      d?.value ??
      d?.avg ??
      d?.average ??
      d?.score ??
      d?.fis_input ??
      d?.data?.value ??
      d?.data?.avg ??
      d?.data?.average ??
      d?.data?.rate ??
      d?.data?.percentage ??
      0
  );
};

const fetchServices = async () => {
  try {
    loading.value = true;

    const [srv01Res, srv03Res, srv04Res, srv05Res, srv06Res, facilitiesRes] =
      await Promise.all([
        api.get("/health-facilities/serviceDisruptionRate"),

        api.get("/health-facilities/mnhIndex"),

        api.get("/health-facilities/essentialMedicinesAvailabilityRate"),

        api.get("/health-facilities/servicePackageComprehensivenessIndex"),

        api.get("/health-facilities/emergencyreadinessIndex"),

        api.get("/health-facilities"),
      ]);

    srv01.value = getApiNumber(srv01Res);

    srv03.value = getApiNumber(srv03Res);

    srv04.value = getApiNumber(srv04Res);

    srv05.value = getApiNumber(srv05Res);

    srv06.value = getApiNumber(srv06Res);

    srv03.value = Number(
      srv03Res.data?.average ?? srv03Res.data?.summary?.MNH_avg ?? 0
    );

    srv03Components.value = (srv03Res.data?.components || []).map((x) => ({
      label: `${x.component} (${x.weight})`,

      value: Number(x.avg || 0),
    }));

    srv06.value = Number(
      srv06Res.data?.average ?? srv06Res.data?.summary?.SRV06_avg ?? 0
    );

    srv06Components.value = (srv06Res.data?.components || []).map((item) => ({
      label: `${item.component} (${item.weight})`,

      value: Number(item.avg || 0),
    }));

    healthFacilities.value = normalizeArray(facilitiesRes.data).map(
      (facility) => ({
        id: facility.id,

        survey_id: facility.survey_id,

        facility_key: facility.survey_id || facility.id,

        facility_name_ar:
          facility.facility_name_ar ||
          facility.facility_name ||
          facility.name ||
          "-",

        facility_name_en:
          facility.facility_name_en ||
          facility.facility_name_ar ||
          facility.facility_name ||
          facility.name ||
          "-",

        facility_type_ar:
          facility.facility_type_ar || facility.facility_type || "-",

        facility_type_en:
          facility.facility_type_en ||
          facility.facility_type_ar ||
          facility.facility_type ||
          "-",
      })
    );
  } catch (error) {
    console.error(error);

    healthFacilities.value = [];
  } finally {
    loading.value = false;
  }
};

const axisWeight = ref(null);

const axisAverage = ref(null);

const loadAxisWeight = async () => {
  try {
    const response = await api.get("/health-facilities/axis-averages");

    const payload = response.data?.data;

    axisWeight.value = payload?.services?.weight ?? null;

    axisAverage.value = payload?.services?.average ?? null;
  } catch (error) {
    console.error("خطأ في تحميل بيانات المحاور:", error);

    axisWeight.value = null;

    axisAverage.value = null;
  }
};

onMounted(async () => {
  await Promise.allSettled([fetchServices(), loadAxisWeight()]);
});

const isInterrupted = (status) => {
  return [
    "متوقف",

    "لا يعمل",

    "معطل",

    "غير متوفر",

    "out_of_service",

    "stopped",

    "not_available",

    "none",

    "not_functional",

    "not_working",
  ].includes(status);
};

const riskWeight = {
  حرج: 5,

  مرتفع: 4,

  عالي: 4,

  high: 4,

  متوسط: 3,

  medium: 3,

  منخفض: 2,

  خفيف: 2,

  low: 2,

  "غير محدد": 1,
};

const getHighestRisk = (current, next) => {
  return (riskWeight[next] || 1) > (riskWeight[current] || 1) ? next : current;
};

const facilities = computed(() => {
  const map = {};

  healthFacilities.value.forEach((facility) => {
    const key = facility.facility_key;

    map[key] = {
      facility_key: key,

      facility_name: isArabic.value
        ? facility.facility_name_ar
        : facility.facility_name_en,

      total_services: 0,

      interrupted_services: 0,

      pre_visits: 0,

      post_visits: 0,

      highest_risk: "غير محدد",
    };
  });

  services.value.forEach((item) => {
    const key = item.facility_key;

    if (!map[key]) {
      map[key] = {
        facility_key: key,

        facility_name: item.facility_name || "غير محدد",

        total_services: 0,

        interrupted_services: 0,

        pre_visits: 0,

        post_visits: 0,

        highest_risk: "غير محدد",
      };
    }

    map[key].total_services += 1;

    if (isInterrupted(item.service_status)) {
      map[key].interrupted_services += 1;
    }

    map[key].pre_visits += item.pre_crisis_visits;

    map[key].post_visits += item.post_crisis_visits;

    map[key].highest_risk = getHighestRisk(
      map[key].highest_risk,

      item.risk_level
    );
  });

  return Object.values(map).map((item) => {
    const drop =
      item.pre_visits > 0
        ? ((item.pre_visits - item.post_visits) / item.pre_visits) * 100
        : 0;

    return {
      ...item,

      drop_rate: Math.max(0, drop).toFixed(1),
    };
  });
});

const serviceCharts = computed(() => ({
  "SRV-01": {
    type: "radialBar",

    label: tr("averageServiceDisruption"),

    value: srv01.value,
  },

  "SRV-03": {
    type: "bar",

    horizontal: true,

    label: tr("mnhIndex"),

    labels: srv03Components.value.map((x) => x.label),

    data: srv03Components.value.map((x) => x.value),
  },

  "SRV-04": {
    type: "radialBar",

    label: tr("averageMedicinesAvailability"),

    value: srv04.value,
  },

  "SRV-05": {
    type: "radialBar",

    label: tr("spciByFacility"),

    value: srv05.value,
  },

  "SRV-06": {
    type: "bar",

    horizontal: true,

    label: tr("emergencyReadinessIndex"),

    labels: srv06Components.value.map((x) => x.label),

    data: srv06Components.value.map((x) => x.value),
  },
}));

const serviceKpis = computed(() => [
  {
    code: "SRV-01",

    name: tr("kpis.serviceDisruption"),

    val: srv01.value,

    unit: "%",

    std: 25,

    dir: "lt",

    max: 100,

    composite: true,

    flag: srv01.value >= 25,

    chart: serviceCharts.value["SRV-01"],
  },

  {
    code: "SRV-03",

    name: tr("kpis.mnh"),

    val: srv03.value,

    unit: "/100",

    std: 80,

    dir: "gt",

    max: 100,

    composite: true,

    flag: srv03.value < 60,

    chart: serviceCharts.value["SRV-03"],
  },

  {
    code: "SRV-04",

    name: tr("kpis.medicines"),

    val: srv04.value,

    unit: "%",

    std: 70,

    dir: "gt",

    max: 100,

    composite: false,

    flag: srv04.value < 70,

    chart: serviceCharts.value["SRV-04"],
  },

  {
    code: "SRV-05",

    name: tr("kpis.spci"),

    val: srv05.value,

    unit: "/100",

    std: 80,

    dir: "gt",

    max: 100,

    composite: true,

    flag: srv05.value < 60,

    chart: serviceCharts.value["SRV-05"],
  },

  {
    code: "SRV-06",

    name: tr("kpis.emergency"),

    val: srv06.value,

    unit: "/100",

    std: 80,

    dir: "gt",

    max: 100,

    composite: true,

    flag: srv06.value < 60,

    chart: serviceCharts.value["SRV-06"],
  },
]);

const kpiStatus = (kpi) => {
  const v = Number(kpi.val || 0);

  const std = Number(kpi.std || 0);

  if (kpi.dir === "lt") {
    if (v <= std) return "g";

    // حرج عندما تتجاوز القيمة المعيار بنسبة 30% أو أكثر.
    if (v < std * 1.3) return "a";

    return "r";
  }

  if (v >= std) return "g";

  // حرج عندما تقل القيمة عن المعيار بنسبة 30% أو أكثر.
  if (v > std * 0.7) return "a";

  return "r";
};

const statusColor = (kpi) => colors[kpiStatus(kpi)];

const achieve = (kpi) => {
  const v = Number(kpi.val || 0);

  const std = Number(kpi.std || 1);

  const max = Number(kpi.max || 100);

  if (kpi.dir === "lt") {
    return v <= std
      ? 100
      : Math.max(
          0,

          Math.round(100 - ((v - std) / Math.max(1, max - std)) * 100)
        );
  }

  return Math.min(100, Math.round((v / std) * 100));
};

const barWidth = (kpi) =>
  Math.min(
    100,

    Math.max(3, (Number(kpi.val || 0) / Number(kpi.max || 100)) * 100)
  );

const serviceCriticalCount = computed(
  () => serviceKpis.value.filter((k) => kpiStatus(k) === "r").length
);

const countBy = (rows, field) => {
  const result = {};

  rows.forEach((item) => {
    const value = item[field] || "غير محدد";

    result[value] = (result[value] || 0) + 1;
  });

  return result;
};

const riskLevelData = computed(() => countBy(facilities.value, "highest_risk"));

const riskLevelLabels = computed(() => Object.keys(riskLevelData.value));

const riskLevelSeries = computed(() => Object.values(riskLevelData.value));

const statusData = computed(() => countBy(services.value, "service_status"));

const statusLabels = computed(() => Object.keys(statusData.value));

const statusSeries = computed(() => Object.values(statusData.value));

const facilityVisitsSeries = computed(() => [
  {
    name: tr("beforeCrisis"),

    data: facilities.value.map((item) => item.pre_visits),
  },

  {
    name: tr("afterCrisis"),

    data: facilities.value.map((item) => item.post_visits),
  },
]);

const riskItems = computed(() => [
  ...new Set(facilities.value.map((x) => x.highest_risk).filter(Boolean)),
]);

const filteredFacilities = computed(() => {
  return facilities.value.filter((item) => {
    const search = filters.value.search?.trim()?.toLowerCase();

    if (search && !item.facility_name.toLowerCase().includes(search)) {
      return false;
    }

    if (filters.value.risk && item.highest_risk !== filters.value.risk) {
      return false;
    }

    return true;
  });
});

const pageCount = computed(() =>
  Math.max(1, Math.ceil(filteredFacilities.value.length / itemsPerPage.value))
);

const paginatedFacilities = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;

  return filteredFacilities.value.slice(start, start + itemsPerPage.value);
});

watch(
  () => [filters.value.search, filters.value.risk, itemsPerPage.value],

  () => {
    currentPage.value = 1;
  }
);

const riskClass = (value) => {
  if (["منخفض", "خفيف", "low"].includes(value)) return "green";

  if (["متوسط", "medium"].includes(value)) return "orange";

  if (value === "غير محدد") return "gray";

  return "red";
};

const miniBaseChart = {
  chart: {
    toolbar: { show: false },

    fontFamily: "Cairo",

    foreColor: "#7890ad",
  },

  dataLabels: { enabled: false },

  legend: { position: "bottom" },

  grid: { borderColor: "#d7e0eb" },

  noData: { text: tr("noData") },
};

const miniBarOptions = (labels = [], horizontal = false) => ({
  ...miniBaseChart,

  plotOptions: {
    bar: { horizontal, borderRadius: 6, columnWidth: "45%" },
  },

  xaxis: {
    categories: labels.length ? labels : [tr("noData")],

    labels: { rotate: horizontal ? 0 : -35, trim: true },
  },
});

const miniDonutOptions = (labels = []) => ({
  ...miniBaseChart,

  labels: labels.length ? labels : [tr("noData")],
});

const radialOptions = (value) => ({
  ...miniBaseChart,

  chart: {
    type: "radialBar",

    toolbar: { show: false },

    fontFamily: "Cairo",
  },

  plotOptions: {
    radialBar: {
      startAngle: -135,

      endAngle: 135,

      hollow: { size: "62%" },

      track: { background: "#e2e8f0" },

      dataLabels: {
        name: { show: false },

        value: {
          fontSize: "30px",

          fontWeight: 900,

          color: value >= 80 ? "#10b981" : value >= 50 ? "#f59e0b" : "#ef4444",

          formatter: (val) => `${Number(val).toFixed(1)}%`,
        },
      },
    },
  },

  colors: [value >= 80 ? "#10b981" : value >= 50 ? "#f59e0b" : "#ef4444"],
});

const kpiApex = (kpi) => {
  const ch = kpi.chart;

  if (!ch) {
    return {
      type: "bar",

      series: [],

      options: miniBarOptions([]),
    };
  }

  if (ch.type === "radialBar") {
    const value = Number(ch.value || 0);

    return {
      type: "radialBar",

      series: [value],

      options: radialOptions(value),
    };
  }

  if (ch.type === "donut") {
    return {
      type: "donut",

      series: ch.data?.length ? ch.data : [0],

      options: miniDonutOptions(ch.labels || []),
    };
  }

  return {
    type: "bar",

    series: [{ name: kpi.code, data: ch.data?.length ? ch.data : [0] }],

    options: miniBarOptions(ch.labels || [], !!ch.horizontal),
  };
};

const donutOptions = (labels = []) => ({
  chart: {
    type: "donut",

    toolbar: { show: false },

    fontFamily: "Cairo",
  },

  labels: labels.length ? labels : [tr("unspecified")],

  legend: { position: "bottom" },

  dataLabels: { enabled: true },

  noData: { text: tr("noData") },
});

const barOptions = (labels = []) => ({
  chart: {
    type: "bar",

    toolbar: { show: false },

    fontFamily: "Cairo",
  },

  xaxis: {
    categories: labels.length ? labels : [tr("unspecified")],

    labels: { rotate: -45 },
  },

  plotOptions: {
    bar: {
      horizontal: false,

      borderRadius: 6,
    },
  },

  dataLabels: { enabled: false },

  noData: { text: tr("noData") },
});
</script>

<style scoped>
.service-page {
  min-height: 100vh;

  background: #f4f6f9;

  padding: 24px;

  color: #071126;

  font-family: "Cairo";
}

.page-loading {
  position: fixed;

  inset: 0;

  background: rgba(244, 246, 249, 0.92);

  backdrop-filter: blur(6px);

  z-index: 9999;

  display: flex;

  align-items: center;

  justify-content: center;
}

.loader-card {
  width: 320px;

  background: #fff;

  border: 1px solid #dbe3ee;

  border-radius: 22px;

  padding: 34px 24px;

  text-align: center;

  box-shadow: 0 20px 60px rgba(15, 35, 64, 0.15);
}

.loader-ring {
  width: 64px;

  height: 64px;

  border: 6px solid #e2e8f0;

  border-top-color: #0f766e;

  border-radius: 50%;

  margin: 0 auto 18px;

  animation: spin 0.8s linear infinite;
}

.loader-card h3 {
  font-size: 18px;

  font-weight: 900;

  color: #0f766e;

  margin-bottom: 8px;
}

.loader-card p {
  color: #64748b;

  font-size: 13px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.page-header {
  text-align: right;

  margin-bottom: 26px;
}

.page-header h1 {
  font-size: 28px;

  font-weight: 900;

  margin-bottom: 8px;

  color: #061226;
}

.page-header p {
  color: #52657d;

  font-size: 14px;
}

.top-row {
  display: flex;

  align-items: center;

  justify-content: space-between;

  margin-bottom: 18px;
}

.counter {
  color: #64748b;

  font-size: 14px;
}

.top-row select,
.filter-box select,
.filter-box input,
.pager-controls select {
  height: 50px;

  background: #fff;

  border: 1px solid #cbd5e1;

  border-radius: 8px;

  padding: 0 14px;

  font-family: inherit;

  outline: none;
}

.top-row select {
  width: 225px;
}

.axis-summary {
  background: #fff;

  border: 1px solid #dbe3ee;

  border-radius: 18px;

  padding: 18px;

  margin-bottom: 18px;

  box-shadow: 0 8px 24px rgba(15, 35, 64, 0.05);
}

.axis-card-header {
  display: flex;

  justify-content: space-between;

  gap: 20px;

  align-items: center;
}

.axis-card-header h2 {
  font-size: 22px;

  font-weight: 900;

  color: #0f766e;

  margin-bottom: 6px;
}

.axis-card-header p {
  color: #64748b;

  font-size: 13px;

  margin-bottom: 8px;
}

.axis-card-header strong {
  color: #071126;

  font-size: 14px;
}

.axis-score {
  min-width: 130px;

  text-align: center;

  background: #ecfdf5;

  color: #0f766e;

  border-radius: 18px;

  padding: 18px;
}

.axis-score span {
  display: block;

  font-size: 34px;

  font-weight: 900;
}

.axis-score small {
  font-weight: 800;
}

.axis-stats {
  display: grid;

  grid-template-columns: repeat(3, 1fr);

  gap: 12px;

  margin-top: 16px;
}

.axis-stats div {
  background: #f8fafc;

  border: 1px solid #e2e8f0;

  border-radius: 14px;

  padding: 14px;

  text-align: center;
}

.axis-stats b {
  display: block;

  font-size: 26px;

  color: #0f766e;
}

.axis-stats span {
  font-size: 13px;

  font-weight: 800;

  color: #64748b;
}

.service-kpi-grid {
  display: grid;

  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));

  gap: 14px;

  margin-bottom: 18px;
}

.service-kpi-card {
  background: #fff;

  border: 1px solid #dbe3ee;

  border-radius: 16px;

  padding: 16px;

  position: relative;

  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
}

.service-kpi-card.flag {
  border-color: rgba(239, 68, 68, 0.38);
}

.service-kpi-card.flag::after {
  content: attr(data-critical-label);

  position: absolute;

  top: 13px;

  left: 14px;

  font-size: 10px;

  color: #ef4444;

  font-weight: 900;
}

.service-kpi-top {
  display: flex;

  gap: 8px;

  margin-bottom: 10px;
}

.composite {
  background: #f5f3ff;

  border: 1px solid #ddd6fe;

  color: #7c3aed;

  border-radius: 7px;

  padding: 3px 8px;

  font-size: 10px;

  font-weight: 900;
}

.service-kpi-card h3 {
  font-size: 15px;

  font-weight: 900;

  color: #071126;

  margin: 0 0 12px;

  min-height: 42px;
}

.service-kpi-value {
  display: flex;

  align-items: baseline;

  gap: 7px;

  margin-bottom: 10px;
}

.service-kpi-value strong {
  font-size: 32px;

  font-weight: 900;

  direction: ltr;
}

.service-kpi-value span {
  color: #64748b;
}

.service-kpi-value em {
  margin-right: auto;

  font-style: normal;

  background: #f8fafc;

  border: 1px solid #e2e8f0;

  border-radius: 8px;

  padding: 5px 9px;

  color: #475569;

  font-size: 11px;

  font-weight: 800;
}

.gauge {
  height: 9px;

  background: #e2e8f0;

  border-radius: 999px;

  overflow: hidden;
}

.gauge div {
  height: 100%;

  border-radius: 999px;
}

.service-kpi-foot {
  display: flex;

  justify-content: space-between;

  align-items: center;

  margin: 8px 0 0;

  font-size: 12px;

  font-weight: 700;

  color: #64748b;
}

.rag {
  border-radius: 7px;

  padding: 4px 10px;

  font-weight: 900;
}

.rag.g {
  background: rgba(16, 185, 129, 0.12);

  color: #10b981;
}

.rag.a {
  background: rgba(245, 158, 11, 0.14);

  color: #f59e0b;
}

.rag.r {
  background: rgba(239, 68, 68, 0.12);

  color: #ef4444;
}

.axis-card {
  background: #fff;

  border: 1px solid #dbe3ee;

  border-radius: 12px;

  padding: 22px;

  box-shadow: 0 1px 4px rgba(15, 35, 64, 0.05);
}

.section-title {
  font-size: 17px;

  font-weight: 900;

  color: #0f766e;

  margin-bottom: 12px;
}

.charts-grid {
  display: grid;

  grid-template-columns: repeat(2, 1fr);

  gap: 14px;

  margin-bottom: 16px;
}

.chart-card,
.services-table-wrap {
  background: #f8fafc;

  border: 1px solid #dbe3ee;

  border-radius: 12px;
}

.chart-card {
  padding: 16px;

  min-height: 370px;
}

.empty-chart,
.empty {
  text-align: center;

  color: #64748b;

  font-weight: 800;

  padding: 26px;
}

.filter-box {
  display: grid;

  grid-template-columns: 1.5fr 1fr;

  gap: 12px;

  margin-bottom: 16px;
}

.services-table-wrap {
  margin-bottom: 18px;

  overflow-x: auto;
}

.table-title {
  background: #0f766e;

  color: #fff;

  padding: 14px 18px;

  margin-bottom: 0;

  display: flex;

  justify-content: space-between;

  font-size: 17px;

  font-weight: 900;
}

.table-title span {
  background: rgba(255, 255, 255, 0.15);

  padding: 4px 10px;

  border-radius: 999px;

  font-size: 12px;
}

.services-table,
.table-wrap table {
  width: 100%;

  min-width: 100%;

  border-collapse: collapse;

  background: #fff;
}

.services-table th {
  background: #f8fafc;

  color: #0f766e;

  font-weight: 900;

  padding: 13px;

  border-bottom: 1px solid #e2e8f0;

  text-align: right;

  white-space: nowrap;
}

.services-table td {
  padding: 13px;

  border-bottom: 1px solid #eef2f7;

  font-size: 13px;

  white-space: nowrap;
}

.pagination-footer {
  display: flex;

  justify-content: space-between;

  align-items: center;

  padding: 14px 18px;

  color: #64748b;

  flex-wrap: wrap;

  gap: 12px;
}

.pager-controls {
  display: flex;

  align-items: center;

  gap: 8px;
}

.pager-controls button {
  height: 38px;

  border: none;

  border-radius: 8px;

  padding: 0 14px;

  background: #0f766e;

  color: #fff;

  cursor: pointer;

  font-family: inherit;
}

.pager-controls button:disabled {
  background: #cbd5e1;

  cursor: not-allowed;
}

.chip {
  display: inline-flex;

  padding: 6px 12px;

  border-radius: 999px;

  font-size: 12px;

  font-weight: 800;
}

.chip.green {
  background: #e8f5e9;

  color: #1b5e20;
}

.chip.orange {
  background: #fff4e5;

  color: #d4821a;
}

.chip.red {
  background: #fdecea;

  color: #c62828;
}

.chip.gray {
  background: #f1f5f9;

  color: #475569;
}

.table-wrap {
  overflow-x: auto;

  border: 1px solid #dbe3ee;

  border-radius: 10px;

  margin-bottom: 18px;

  background: #fff;
}

.table-wrap table {
  min-width: 1200px;
}

.indicators-table table {
  min-width: 1300px;
}

.table-wrap th {
  background: #f8fafc;

  color: #0f766e;

  font-size: 13px;

  font-weight: 900;

  padding: 15px 14px;

  text-align: right;

  white-space: nowrap;

  border-bottom: 1px solid #e2e8f0;
}

.table-wrap td {
  padding: 14px;

  border-bottom: 1px solid #dbe3ee;

  font-size: 13px;

  line-height: 1.8;

  vertical-align: top;
}

tbody tr:hover {
  background: #f8fafc;
}

.code {
  font-weight: 900;

  direction: ltr;

  text-align: center;

  white-space: nowrap;

  color: #0f766e;

  background: #ecfdf5;

  border: 1px solid #bbf7d0;

  border-radius: 7px;

  padding: 3px 8px;

  font-size: 11px;
}

.name {
  font-weight: 900;

  min-width: 220px;
}

.unit {
  text-align: center;

  white-space: nowrap;
}

@media (max-width: 1100px) {
  .charts-grid,
  .filter-box,
  .axis-stats {
    grid-template-columns: 1fr;
  }

  .axis-card-header {
    flex-direction: column;

    align-items: stretch;
  }
}

@media (max-width: 900px) {
  .service-page {
    padding: 16px 12px;
  }

  .page-header h1 {
    font-size: 21px;
  }

  .top-row {
    flex-direction: column-reverse;

    align-items: stretch;

    gap: 12px;
  }

  .top-row select {
    width: 100%;
  }

  .axis-card {
    padding: 14px;
  }
}

/* ===== Indicator mini cards copied from axis indicators page ===== */

.axis-indicator-grid {
  display: grid;

  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));

  gap: 14px;

  margin-bottom: 18px;
}

.axis-indicator-card {
  background: #fff;

  border: 1px solid #dbe3ee;

  border-radius: 16px;

  padding: 16px;

  position: relative;

  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
}

.axis-indicator-card.flag {
  border-color: rgba(239, 68, 68, 0.38);
}

.axis-indicator-card.flag::after {
  content: attr(data-critical-label);

  position: absolute;

  top: 13px;

  left: 14px;

  font-size: 10px;

  color: #ef4444;

  font-weight: 900;
}

.axis-indicator-top {
  display: flex;

  gap: 8px;

  margin-bottom: 10px;
}

.axis-indicator-card h3 {
  font-size: 15px;

  font-weight: 900;

  color: #071126;

  margin: 0 0 12px;

  min-height: 42px;
}

.axis-indicator-value {
  display: flex;

  align-items: baseline;

  gap: 7px;

  margin-bottom: 10px;
}

.axis-indicator-value strong {
  font-size: 32px;

  font-weight: 900;

  direction: ltr;
}

.axis-indicator-value span {
  color: #64748b;
}

.axis-indicator-value em {
  margin-right: auto;

  font-style: normal;

  background: #f8fafc;

  border: 1px solid #e2e8f0;

  border-radius: 8px;

  padding: 5px 9px;

  color: #475569;

  font-size: 11px;

  font-weight: 800;
}

.axis-indicator-foot {
  display: flex;

  justify-content: space-between;

  align-items: center;

  margin: 8px 0 13px;

  font-size: 12px;

  font-weight: 700;

  color: #64748b;
}

.axis-mini-chart {
  background: #f8fafc;

  border: 1px solid #dbe3ee;

  border-radius: 14px;

  padding: 14px;
}

.axis-mini-chart h4 {
  font-size: 14px;

  font-weight: 900;

  color: inherit;

  margin: 0 0 10px;
}

.composite {
  background: #f5f3ff;

  border: 1px solid #ddd6fe;

  color: #7c3aed;

  border-radius: 7px;

  padding: 3px 8px;

  font-size: 10px;

  font-weight: 900;
}

.gauge {
  height: 9px;

  background: #e2e8f0;

  border-radius: 999px;

  overflow: hidden;
}

.gauge div {
  height: 100%;

  border-radius: 999px;
}

.rag {
  border-radius: 7px;

  padding: 4px 10px;

  font-weight: 900;
}

.rag.g {
  background: rgba(16, 185, 129, 0.12);

  color: #10b981;
}

.rag.a {
  background: rgba(245, 158, 11, 0.14);

  color: #f59e0b;
}

.rag.r {
  background: rgba(239, 68, 68, 0.12);

  color: #ef4444;
}

.mobile-facility-cards {
  display: none;
}

@media (max-width: 768px) {
  .service-page {
    padding: 12px 8px 28px;

    overflow-x: hidden;
  }

  .page-header {
    margin-bottom: 16px;
  }

  .page-header h1 {
    font-size: 20px;

    line-height: 1.5;
  }

  .page-header p {
    font-size: 12px;

    line-height: 1.8;
  }

  .axis-summary {
    padding: 13px;

    border-radius: 15px;
  }

  .axis-card-header {
    flex-direction: column;

    align-items: stretch;

    gap: 12px;
  }

  .axis-card-header h2 {
    font-size: 18px;
  }

  .axis-score {
    width: 100%;

    min-width: 0;

    padding: 13px;

    border-radius: 14px;
  }

  .axis-score span {
    font-size: 28px;
  }

  .axis-stats {
    grid-template-columns: repeat(3, minmax(0, 1fr));

    gap: 7px;
  }

  .axis-stats div {
    padding: 10px 5px;

    border-radius: 11px;
  }

  .axis-stats b {
    font-size: 21px;
  }

  .axis-stats span {
    font-size: 10px;

    line-height: 1.5;
  }

  .service-kpi-grid {
    grid-template-columns: minmax(0, 1fr);

    gap: 12px;
  }

  .service-kpi-card {
    padding: 13px;
  }

  .service-kpi-card h3 {
    min-height: 0;

    font-size: 14px;
  }

  .service-kpi-value {
    flex-wrap: wrap;
  }

  .service-kpi-value strong {
    font-size: 28px;
  }

  .service-kpi-value em {
    width: 100%;

    margin-right: 0;

    text-align: center;
  }

  .axis-mini-chart {
    padding: 8px;

    overflow-x: auto;

    -webkit-overflow-scrolling: touch;
  }

  .axis-mini-chart :deep(.apexcharts-canvas) {
    max-width: 100% !important;
  }

  .axis-card {
    padding: 12px 8px;
  }

  .charts-grid,
  .filter-box {
    grid-template-columns: minmax(0, 1fr);
  }

  .services-table {
    display: none;
  }

  .services-table-wrap {
    overflow: visible;

    border: 0;

    background: transparent;
  }

  .table-title {
    padding: 12px 13px;

    border-radius: 12px;

    font-size: 14px;
  }

  .mobile-facility-cards {
    display: grid;

    gap: 12px;

    margin-top: 12px;
  }

  .mobile-facility-card {
    padding: 14px;

    background: #fff;

    border: 1px solid #dbe3ee;

    border-inline-start: 5px solid #0f766e;

    border-radius: 15px;

    box-shadow: 0 6px 18px rgba(15, 35, 64, 0.06);
  }

  .mobile-card-head {
    display: flex;

    align-items: flex-start;

    justify-content: space-between;

    gap: 10px;

    padding-bottom: 12px;

    margin-bottom: 4px;

    border-bottom: 1px solid #e2e8f0;
  }

  .mobile-card-head h3 {
    min-width: 0;

    margin: 0;

    color: #0f766e;

    font-size: 15px;

    font-weight: 900;

    line-height: 1.7;

    overflow-wrap: break-word;
  }

  .mobile-card-head .chip {
    flex-shrink: 0;
  }

  .mobile-card-body {
    display: grid;
  }

  .mobile-data-row {
    display: flex;

    align-items: center;

    justify-content: space-between;

    gap: 14px;

    padding: 10px 0;

    border-bottom: 1px dashed #e2e8f0;
  }

  .mobile-data-row:last-child {
    border-bottom: 0;
  }

  .mobile-data-row span {
    color: #64748b;

    font-size: 12px;

    font-weight: 800;
  }

  .mobile-data-row strong {
    direction: ltr;

    color: #071126;

    font-size: 14px;

    font-weight: 900;
  }

  .mobile-empty {
    padding: 24px;

    text-align: center;

    color: #64748b;

    background: #fff;

    border: 1px dashed #cbd5e1;

    border-radius: 14px;

    font-weight: 800;
  }

  .pagination-footer {
    flex-direction: column;

    align-items: stretch;

    padding: 12px 0;
  }

  .pager-controls {
    width: 100%;

    justify-content: space-between;
  }

  .pager-controls button {
    flex: 1;
  }

  .loader-card {
    width: calc(100% - 28px);

    padding: 28px 18px;
  }
}

@media (max-width: 380px) {
  .axis-stats {
    grid-template-columns: 1fr;
  }

  .mobile-card-head {
    flex-direction: column;
  }

  .pager-controls {
    flex-wrap: wrap;
  }
}

/* ================= RTL / LTR ================= */

.service-page[dir="rtl"] {
  direction: rtl;

  font-family: "Cairo", sans-serif;
}

.service-page[dir="ltr"] {
  direction: ltr;

  font-family: "Inter", sans-serif;
}

.service-page[dir="rtl"] .page-header,
.service-page[dir="rtl"] .table-title,
.service-page[dir="rtl"] .section-title,
.service-page[dir="rtl"] th,
.service-page[dir="rtl"] td {
  text-align: right;
}

.service-page[dir="ltr"] .page-header,
.service-page[dir="ltr"] .table-title,
.service-page[dir="ltr"] .section-title,
.service-page[dir="ltr"] th,
.service-page[dir="ltr"] td {
  text-align: left;
}

.service-page[dir="ltr"] .service-kpi-foot,
.service-page[dir="ltr"] .axis-indicator-foot,
.service-page[dir="ltr"] .mobile-data-row,
.service-page[dir="ltr"] .mobile-card-head,
.service-page[dir="ltr"] .table-title,
.service-page[dir="ltr"] .top-row,
.service-page[dir="ltr"] .axis-card-header {
  direction: ltr;
}

.service-page[dir="rtl"] .service-kpi-foot,
.service-page[dir="rtl"] .axis-indicator-foot,
.service-page[dir="rtl"] .mobile-data-row,
.service-page[dir="rtl"] .mobile-card-head,
.service-page[dir="rtl"] .table-title,
.service-page[dir="rtl"] .top-row,
.service-page[dir="rtl"] .axis-card-header {
  direction: rtl;
}

.service-page[dir="ltr"] .mobile-facility-card {
  border-inline-start: 0;

  border-inline-end: 5px solid #0f766e;
}

.service-page[dir="rtl"] .mobile-facility-card {
  border-inline-end: 0;

  border-inline-start: 5px solid #0f766e;
}

.service-page[dir="ltr"] .service-kpi-card.flag::after,
.service-page[dir="ltr"] .axis-indicator-card.flag::after {
  left: auto;

  right: 14px;
}

.service-page[dir="rtl"] .service-kpi-card.flag::after,
.service-page[dir="rtl"] .axis-indicator-card.flag::after {
  right: auto;

  left: 14px;
}

.service-page[dir="ltr"] .service-kpi-value em,
.service-page[dir="ltr"] .axis-indicator-value em {
  margin-left: auto;

  margin-right: 0;
}

.service-page[dir="rtl"] .service-kpi-value em,
.service-page[dir="rtl"] .axis-indicator-value em {
  margin-right: auto;

  margin-left: 0;
}

.service-page[dir="ltr"] .chip {
  margin-left: auto;
}

.service-page[dir="rtl"] .chip {
  margin-right: auto;
}
</style>
